import "./imageAdmin.css";
import { useState, useEffect } from "react";
import { getImagesConfig, saveImagesConfig } from "../../config/imageConfig";
import images from "../Team/teamImages.js";
import chambres from "../Team/teamImagesChambre.js";
import logoFooter from "../../assets/images/logo/Logo_Carpe_Diem.png";
import {
  fetchImagesConfigFromApi,
  saveImagesConfigToApi,
} from "../../utils/contentApi";

function ImageAdmin() {
  const [imagesConfig, setImagesConfig] = useState(getImagesConfig());
  const defaultRestaurantTitles = [
    "Bar Mojito",
    "Bar et sièges",
    "Carte",
    "Décoration de la Salle",
    "Horloge de la Salle",
    "Salle à manger de jour",
    "Salle à manger de nuit",
    "Terrasse",
  ];
  const defaultChambresTitles = [
    "Lit double",
    "Salle de bain",
    "Seconde Chambre lit",
    "Seconde Chambre",
    "Télé de la seconde Chambre",
    "Évier Salle de bain",
  ];

  const getGalleryTitles = (galleryKey) =>
    galleryKey === "galerieRestaurant"
      ? defaultRestaurantTitles
      : defaultChambresTitles;

  useEffect(() => {
    let isMounted = true;

    const loadConfig = async () => {
      const localConfig = getImagesConfig();
      setImagesConfig(localConfig);

      try {
        const remoteConfig = await fetchImagesConfigFromApi();
        if (!isMounted || !remoteConfig || Object.keys(remoteConfig).length === 0) {
          return;
        }

        setImagesConfig(remoteConfig);
        saveImagesConfig(remoteConfig);
      } catch (_error) {
        // Keep local cache if API is unavailable.
      }
    };

    loadConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  const persistConfig = async (nextConfig) => {
    setImagesConfig(nextConfig);
    saveImagesConfig(nextConfig);

    try {
      const persistedConfig = await saveImagesConfigToApi(nextConfig);
      if (persistedConfig && Object.keys(persistedConfig).length > 0) {
        setImagesConfig(persistedConfig);
        saveImagesConfig(persistedConfig);
      }
      window.dispatchEvent(new Event("carpe-content-changed"));
      return { success: true };
    } catch (_error) {
      return {
        success: false,
        error:
          "La sauvegarde distante a échoué. Les données restent en cache local.",
      };
    }
  };

  // Gérer l'upload d'une image
  const handleImageUpload = (category, key, file) => {
    if (!file.type.startsWith("image/")) {
      alert("Veuillez sélectionner un fichier image");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert("L'image est trop volumineuse. Taille maximum: 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      updateImage(category, key, reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Mettre à jour une image
  const updateImage = (category, key, value) => {
    const newConfig = { ...imagesConfig };
    newConfig[category][key] = value;
    persistConfig(newConfig);
  };

  // Mettre à jour une image de galerie (array)
  const updateGalleryImage = (galleryKey, index, value) => {
    const newConfig = { ...imagesConfig };

    // Initialiser la galerie si elle n'existe pas
    if (!newConfig[galleryKey]) {
      const titles = getGalleryTitles(galleryKey);

      newConfig[galleryKey] = titles.map((title) => ({
        title,
        src: "",
        alt: "",
      }));
    }

    newConfig[galleryKey][index] = {
      ...newConfig[galleryKey][index],
      src: value,
    };
    persistConfig(newConfig);
  };

  // Mettre à jour le alt d'une image de galerie
  const updateGalleryImageAlt = (galleryKey, index, value) => {
    const newConfig = { ...imagesConfig };

    // Initialiser la galerie si elle n'existe pas
    if (!newConfig[galleryKey]) {
      const titles = getGalleryTitles(galleryKey);

      newConfig[galleryKey] = titles.map((title) => ({
        title,
        src: "",
        alt: "",
        caption: title,
      }));
    }

    newConfig[galleryKey][index] = {
      ...newConfig[galleryKey][index],
      alt: value,
    };
    persistConfig(newConfig);
  };

  // Mettre à jour le caption d'une image de galerie
  const updateGalleryImageCaption = (galleryKey, index, value) => {
    const newConfig = { ...imagesConfig };

    // Initialiser la galerie si elle n'existe pas
    if (!newConfig[galleryKey]) {
      const titles = getGalleryTitles(galleryKey);

      newConfig[galleryKey] = titles.map((title) => ({
        title,
        src: "",
        alt: "",
        caption: title,
      }));
    }

    newConfig[galleryKey][index] = {
      ...newConfig[galleryKey][index],
      caption: value,
    };
    persistConfig(newConfig);
  };

  // Réinitialiser toutes les images
  const resetAllImages = () => {
    if (
      window.confirm(
        "Êtes-vous sûr de vouloir réinitialiser toutes les images aux valeurs par défaut ?",
      )
    ) {
      localStorage.removeItem("carpeDiemImagesConfig");
      const config = getImagesConfig();
      persistConfig(config);
    }
  };

  return (
    <div className="image-admin">
      <div className="admin-header">
        <div>
          <h1>Gestion des Images</h1>
          <p className="header-subtitle">
            Modifiez toutes les images affichées sur le site
          </p>
        </div>
        <button className="btn-reset" onClick={resetAllImages}>
          🔄 Réinitialiser tout
        </button>
      </div>

      {/* Images du Header */}
      <div className="image-category">
        <div className="category-header">
          <h2>📱 Images du Header</h2>
          <p className="category-desc">
            Images de fond affichées dans l'en-tête de chaque page
          </p>
        </div>

        <div className="images-grid">
          <ImageCard
            title="Page d'accueil"
            imageUrl={imagesConfig.header?.home}
            imageAlt={imagesConfig.header?.homeAlt}
            onUpdate={(value) => updateImage("header", "home", value)}
            onUpdateAlt={(value) => updateImage("header", "homeAlt", value)}
            onUpload={(file) => handleImageUpload("header", "home", file)}
          />
          <ImageCard
            title="Page Menu"
            imageUrl={imagesConfig.header?.menu}
            imageAlt={imagesConfig.header?.menuAlt}
            onUpdate={(value) => updateImage("header", "menu", value)}
            onUpdateAlt={(value) => updateImage("header", "menuAlt", value)}
            onUpload={(file) => handleImageUpload("header", "menu", file)}
          />
          <ImageCard
            title="Page Restaurant & Chambres"
            imageUrl={imagesConfig.header?.restaurantChambres}
            imageAlt={imagesConfig.header?.restaurantChambresAlt}
            onUpdate={(value) =>
              updateImage("header", "restaurantChambres", value)
            }
            onUpdateAlt={(value) =>
              updateImage("header", "restaurantChambresAlt", value)
            }
            onUpload={(file) =>
              handleImageUpload("header", "restaurantChambres", file)
            }
          />
          <ImageCard
            title="Page Réservation"
            imageUrl={imagesConfig.header?.reservation}
            imageAlt={imagesConfig.header?.reservationAlt}
            onUpdate={(value) => updateImage("header", "reservation", value)}
            onUpdateAlt={(value) =>
              updateImage("header", "reservationAlt", value)
            }
            onUpload={(file) =>
              handleImageUpload("header", "reservation", file)
            }
          />
          <ImageCard
            title="Page Contact"
            imageUrl={imagesConfig.header?.contact}
            imageAlt={imagesConfig.header?.contactAlt}
            onUpdate={(value) => updateImage("header", "contact", value)}
            onUpdateAlt={(value) => updateImage("header", "contactAlt", value)}
            onUpload={(file) => handleImageUpload("header", "contact", file)}
          />
        </div>
      </div>

      {/* Images de la page Restaurant & Chambres */}
      <div className="image-category">
        <div className="category-header">
          <h2>🍽️ Page Restaurant & Chambres</h2>
          <p className="category-desc">
            Images principales affichées sur la page Team
          </p>
        </div>

        <div className="images-grid">
          <ImageCard
            title="Restaurant - Image principale"
            imageUrl={imagesConfig.team?.restaurantMain}
            imageAlt={imagesConfig.team?.restaurantMainAlt}
            onUpdate={(value) => updateImage("team", "restaurantMain", value)}
            onUpdateAlt={(value) =>
              updateImage("team", "restaurantMainAlt", value)
            }
            onUpload={(file) =>
              handleImageUpload("team", "restaurantMain", file)
            }
          />
          <ImageCard
            title="Chambres - Image principale"
            imageUrl={imagesConfig.team?.chambreMain}
            imageAlt={imagesConfig.team?.chambreMainAlt}
            onUpdate={(value) => updateImage("team", "chambreMain", value)}
            onUpdateAlt={(value) =>
              updateImage("team", "chambreMainAlt", value)
            }
            onUpload={(file) => handleImageUpload("team", "chambreMain", file)}
          />
        </div>
      </div>

      {/* Images de Contact */}
      <div className="image-category">
        <div className="category-header">
          <h2>📍 Page Contact</h2>
          <p className="category-desc">
            Capture d'écran de la carte Google Maps
          </p>
        </div>

        <div className="images-grid">
          <ImageCard
            title="Carte Maps"
            imageUrl={imagesConfig.contact?.mapScreenshot}
            imageAlt={imagesConfig.contact?.mapScreenshotAlt}
            onUpdate={(value) => updateImage("contact", "mapScreenshot", value)}
            onUpdateAlt={(value) =>
              updateImage("contact", "mapScreenshotAlt", value)
            }
            onUpload={(file) =>
              handleImageUpload("contact", "mapScreenshot", file)
            }
          />
        </div>
      </div>

      {/* Images de la page Home */}
      <div className="image-category">
        <div className="category-header">
          <h2>🏠 Page d'accueil</h2>
          <p className="category-desc">
            Images affichées sur la page d'accueil
          </p>
        </div>

        <div className="images-grid">
          <ImageCard
            title="Image Hero 1 (Bar Décoration)"
            imageUrl={imagesConfig.home?.heroImage1}
            imageAlt={imagesConfig.home?.heroImage1Alt}
            onUpdate={(value) => updateImage("home", "heroImage1", value)}
            onUpdateAlt={(value) => updateImage("home", "heroImage1Alt", value)}
            onUpload={(file) => handleImageUpload("home", "heroImage1", file)}
          />
          <ImageCard
            title="Image de fond Section Menu"
            imageUrl={imagesConfig.home?.menuSectionBg}
            imageAlt={imagesConfig.home?.menuSectionBgAlt}
            onUpdate={(value) => updateImage("home", "menuSectionBg", value)}
            onUpdateAlt={(value) =>
              updateImage("home", "menuSectionBgAlt", value)
            }
            onUpload={(file) =>
              handleImageUpload("home", "menuSectionBg", file)
            }
          />
        </div>
      </div>

      {/* Logo Footer */}
      <div className="image-category">
        <div className="category-header">
          <h2>🏷️ Logo & Branding</h2>
          <p className="category-desc">Logo affiché dans le footer du site</p>
        </div>

        <div className="images-grid">
          <ImageCard
            title="Logo Footer"
            imageUrl={imagesConfig.footer?.logo || logoFooter}
            imageAlt={imagesConfig.footer?.logoAlt}
            darkPreview
            onUpdate={(value) => updateImage("footer", "logo", value)}
            onUpdateAlt={(value) => updateImage("footer", "logoAlt", value)}
            onUpload={(file) => handleImageUpload("footer", "logo", file)}
          />
        </div>
      </div>

      {/* Galerie Restaurant */}
      <div className="image-category">
        <div className="category-header">
          <h2>🍽️ Galerie Restaurant</h2>
          <p className="category-desc">
            Images de la galerie du restaurant (8 photos)
          </p>
        </div>

        <div className="images-grid">
          {defaultRestaurantTitles.map((title, index) => (
            <ImageCard
              key={`resto-${index}`}
              title={title}
              imageUrl={
                imagesConfig.galerieRestaurant?.[index]?.src ||
                images[index]?.src ||
                ""
              }
              imageAlt={imagesConfig.galerieRestaurant?.[index]?.alt || ""}
              imageCaption={
                imagesConfig.galerieRestaurant?.[index]?.caption || title
              }
              onUpdate={(value) =>
                updateGalleryImage("galerieRestaurant", index, value)
              }
              onUpdateAlt={(value) =>
                updateGalleryImageAlt("galerieRestaurant", index, value)
              }
              onUpdateCaption={(value) =>
                updateGalleryImageCaption("galerieRestaurant", index, value)
              }
              onUpload={(file) => {
                if (!file.type.startsWith("image/")) {
                  alert("Veuillez sélectionner un fichier image");
                  return;
                }
                if (file.size > 2 * 1024 * 1024) {
                  alert("L'image est trop volumineuse. Taille maximum: 2MB");
                  return;
                }
                const reader = new FileReader();
                reader.onloadend = () => {
                  updateGalleryImage("galerieRestaurant", index, reader.result);
                };
                reader.readAsDataURL(file);
              }}
            />
          ))}
        </div>
      </div>

      {/* Galerie Chambres */}
      <div className="image-category">
        <div className="category-header">
          <h2>🛏️ Galerie Chambres</h2>
          <p className="category-desc">
            Images de la galerie des chambres (6 photos)
          </p>
        </div>

        <div className="images-grid">
          {defaultChambresTitles.map((title, index) => (
            <ImageCard
              key={`chambre-${index}`}
              title={title}
              imageUrl={
                imagesConfig.galerieChambres?.[index]?.src ||
                chambres[index]?.src ||
                ""
              }
              imageAlt={imagesConfig.galerieChambres?.[index]?.alt || ""}
              imageCaption={
                imagesConfig.galerieChambres?.[index]?.caption || title
              }
              onUpdate={(value) =>
                updateGalleryImage("galerieChambres", index, value)
              }
              onUpdateAlt={(value) =>
                updateGalleryImageAlt("galerieChambres", index, value)
              }
              onUpdateCaption={(value) =>
                updateGalleryImageCaption("galerieChambres", index, value)
              }
              onUpload={(file) => {
                if (!file.type.startsWith("image/")) {
                  alert("Veuillez sélectionner un fichier image");
                  return;
                }
                if (file.size > 2 * 1024 * 1024) {
                  alert("L'image est trop volumineuse. Taille maximum: 2MB");
                  return;
                }
                const reader = new FileReader();
                reader.onloadend = () => {
                  updateGalleryImage("galerieChambres", index, reader.result);
                };
                reader.readAsDataURL(file);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Composant pour une carte d'image
function ImageCard({
  title,
  imageUrl,
  imageAlt,
  imageCaption,
  darkPreview = false,
  onUpdate,
  onUpload,
  onUpdateAlt,
  onUpdateCaption,
}) {
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState(imageUrl || "");
  const [altValue, setAltValue] = useState(imageAlt || "");
  const [captionValue, setCaptionValue] = useState(imageCaption || "");
  const [altSaved, setAltSaved] = useState(false);
  const [captionSaved, setCaptionSaved] = useState(false);

  useEffect(() => {
    setUrlValue(imageUrl || "");
    setAltValue(imageAlt || "");
    setCaptionValue(imageCaption || "");
  }, [imageUrl, imageAlt, imageCaption]);

  const handleUrlSave = () => {
    onUpdate(urlValue);
    setShowUrlInput(false);
  };

  const handleAltSave = () => {
    if (onUpdateAlt && !altSaved) {
      onUpdateAlt(altValue);
      setAltSaved(true);
      setTimeout(() => setAltSaved(false), 2000);
    }
  };

  const handleCaptionSave = () => {
    if (onUpdateCaption && !captionSaved) {
      onUpdateCaption(captionValue);
      setCaptionSaved(true);
      setTimeout(() => setCaptionSaved(false), 2000);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      onUpload(file);
    }
  };

  const displayTitle =
    onUpdateCaption && captionValue.trim().length > 0 ? captionValue : title;

  return (
    <div className="image-card">
      <h3 className="image-card-title">{displayTitle}</h3>

      <div
        className={`image-preview-box${darkPreview ? " image-preview-box-dark" : ""}`}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className={`image-preview-admin${darkPreview ? " image-preview-admin-contain" : ""}`}
          />
        ) : (
          <div className="image-placeholder">Aucune image</div>
        )}
      </div>

      <div className="image-actions">
        <label className="btn-upload">
          📁 Depuis l'ordinateur
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </label>

        <button
          className="btn-url"
          onClick={() => setShowUrlInput(!showUrlInput)}
        >
          🔗 URL
        </button>

        {imageUrl && (
          <button className="btn-clear" onClick={() => onUpdate("")}>
            ✕
          </button>
        )}
      </div>

      {showUrlInput && (
        <div className="url-input-section">
          <input
            type="text"
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            placeholder="https://..."
            className="url-input"
          />
          <div className="url-actions">
            <button className="btn-save-url" onClick={handleUrlSave}>
              Sauvegarder
            </button>
            <button
              className="btn-cancel-url"
              onClick={() => {
                setShowUrlInput(false);
                setUrlValue(imageUrl || "");
              }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Champ pour le texte alternatif */}
      {onUpdateAlt && (
        <div className="alt-input-section">
          <label className="alt-label">Texte alternatif (Alt) :</label>
          <div className="alt-input-wrapper">
            <input
              type="text"
              value={altValue}
              onChange={(e) => setAltValue(e.target.value)}
              placeholder="Description de l'image..."
              className="alt-input"
            />
            <button
              className="btn-save-alt"
              onClick={handleAltSave}
              disabled={altSaved}
            >
              {altSaved ? "✓ Sauvegardé" : "💾 Sauvegarder"}
            </button>
          </div>
        </div>
      )}

      {/* Champ pour le caption (figcaption) */}
      {onUpdateCaption && (
        <div className="alt-input-section">
          <label className="alt-label">Légende (Caption) :</label>
          <div className="alt-input-wrapper">
            <input
              type="text"
              value={captionValue}
              onChange={(e) => setCaptionValue(e.target.value)}
              placeholder="Texte affiché sous l'image..."
              className="alt-input"
            />
            <button
              className="btn-save-alt"
              onClick={handleCaptionSave}
              disabled={captionSaved}
            >
              {captionSaved ? "✓ Sauvegardé" : "💾 Sauvegarder"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageAdmin;
